/**
 * Phase 1.3 — Client mirror of modules/battleWsCardDefs.js.
 *
 * Re-expands a gameState that the server compressed into a top-level `cardDefs[orderId]` dictionary.
 * Each stripped instance carries a `_cd` marker; expansion re-attaches the lifted fields via
 * `{ ...cardDefs[id], ...instance }` semantics (instance fields always win), then removes the marker
 * and the dictionary. Byte-identical to what the instance carried before compression.
 *
 * Everything the client needs is inside the same payload (the dictionary is rebuilt per message on
 * the server), so this never depends on a card cache or a prior message — spawned/converted/enemy
 * cards expand from the same message that introduced them.
 */

const CARD_DEF_MARKER = '_cd';

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

function expandNode(node, cardDefs) {
  if (Array.isArray(node)) {
    for (const child of node) {
      expandNode(child, cardDefs);
    }
    return;
  }
  if (!node || typeof node !== 'object') {
    return;
  }

  if (node[CARD_DEF_MARKER] === 1) {
    const def = cardDefs[node.id];
    if (!def || typeof def !== 'object') {
      throw new Error(
        `battleWsCardDefs: compressed instance references missing cardDefs entry for id ${node.id}`
      );
    }
    for (const field of Object.keys(def)) {
      if (!hasOwn(node, field)) {
        node[field] = def[field];
      }
    }
    delete node[CARD_DEF_MARKER];
  }

  for (const key of Object.keys(node)) {
    expandNode(node[key], cardDefs);
  }
}

/**
 * Expand a compressed gameState in place. Returns the same reference (unchanged when no cardDefs).
 * @param {object} gameState
 * @returns {object}
 */
export function decompressGameStateCardDefs(gameState) {
  if (!gameState || typeof gameState !== 'object' || Array.isArray(gameState)) {
    return gameState;
  }
  const cardDefs = gameState.cardDefs;
  if (!cardDefs || typeof cardDefs !== 'object') {
    return gameState;
  }
  for (const key of Object.keys(gameState)) {
    if (key === 'cardDefs') {
      continue;
    }
    expandNode(gameState[key], cardDefs);
  }
  delete gameState.cardDefs;
  return gameState;
}

/**
 * Expand a parsed WS message's gameState (if compressed) in place. Safe no-op for messages without
 * a compressed gameState (attack-initiated-minimal patches, non-battle messages, etc.).
 * @param {object} msg
 * @returns {object} the same msg
 */
export function expandBattleWsMessageCardDefs(msg) {
  if (!msg || typeof msg !== 'object') {
    return msg;
  }
  if (msg.gameState && typeof msg.gameState === 'object' && msg.gameState.cardDefs) {
    decompressGameStateCardDefs(msg.gameState);
  }
  return msg;
}
